import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Button from '../../common/Button/Button';
import ScreenContainer from '../../common/ScreenContainer/ScreenContainer';
import './ResetPassword.css';
import { AppContext, useQueryParam } from '../../../utils/AppContext';
import useAuthentication from '../../../customHooks/useAuthentication';
import { MixpanelContext, SCREENS } from '../../../utils/mixpanel';
import { PasswordRequirements } from './PasswordRequirements/PasswordRequirements';
import { usePasswordRequirements } from './PasswordRequirements/usePasswordRequirement';
import { PasswordField } from '../../common/PasswordField/PaswordField';

export const RESET_USER_KEY = 'resetUser';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [isButtonAnimating, setIsButtonAnimating] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState<string>();
  const [newPasswordErrorKey, setNewPasswordErrorKey] = useState<string | null>(null);
  const [confirmedPasswordErrorKey, setConfirmedPasswordErrorKey] = useState<string | null>(null);
  const history = useHistory();
  const emailToken = useQueryParam('token');
  const { logoURL } = useContext(AppContext);
  const { t } = useTranslation();
  const { resetPassword } = useAuthentication();
  const { sendMixpanelError, sendMixpanelEvent } = useContext(MixpanelContext);
  const requirements = usePasswordRequirements(newPassword);
  const areAllRequirementsSatisfied = requirements.every((requirement) => requirement.isSatisfied);

  const user: { email: string; token: string } | null = useMemo(() => {
    const stringifiedUser = window.localStorage.getItem(RESET_USER_KEY);
    return stringifiedUser ? JSON.parse(stringifiedUser) : stringifiedUser;
  }, []);

  const goToLoginScreen = useCallback(() => {
    // Remove information about the current user before redirecting back to login screen
    window.localStorage.removeItem(RESET_USER_KEY);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('token');
    const searchString = searchParams.toString() ? `?${searchParams.toString()}` : '';

    history.push(`/login${searchString}`);
  }, [history]);

  useEffect(() => {
    if (user === null && emailToken == null) {
      goToLoginScreen();
    }
  }, [user, emailToken, history, goToLoginScreen]);

  const onCreateNewPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessageKey(undefined);
    const validationErrorKeys = passwordValidationErrorKeys({
      newPassword,
      confirmedPassword,
      areAllRequirementsSatisfied,
    });
    setNewPasswordErrorKey(validationErrorKeys.newPassword);
    setConfirmedPasswordErrorKey(validationErrorKeys.confirmedPassword);
    if (
      validationErrorKeys.newPassword !== null ||
      validationErrorKeys.confirmedPassword !== null
    ) {
      sendMixpanelError({
        email: (user?.email ?? emailToken)!,
        errorKey: (validationErrorKeys.newPassword || validationErrorKeys.confirmedPassword)!,
        screen: SCREENS.RESET_PASSWORD,
      });
    } else {
      setIsButtonAnimating(true);
      resetPassword({
        email: user?.email,
        tokenFromLocalStorage: user?.token,
        tokenFromForgotPasswordEmail: emailToken,
        newPassword,
      })
        .then((res) => {
          const userName = res.data?.userName;
          if (userName) {
            sendMixpanelEvent('Reset password completed', {
              email: userName,
              screen: SCREENS.RESET_PASSWORD,
            });
            goToLoginScreen();
          } else {
            setErrorMessageKey('resetErrors.unknown');
            sendMixpanelError({
              email: (user?.email ?? emailToken) as string,
              errorKey: 'resetErrors.unknown',
              screen: SCREENS.RESET_PASSWORD,
            });
          }
        })
        .catch((axiosError) => {
          const category = axiosError.response?.data?.error?.category;
          let errorKey: string;
          if (category === 'incorrectState') {
            errorKey = 'resetErrors.oldPassword';
            setErrorMessageKey(errorKey);
          } else {
            errorKey = 'resetErrors.unknown';
            setErrorMessageKey(errorKey);
          }
          sendMixpanelError({
            email: (user?.email ?? emailToken) as string,
            errorKey,
            axiosError,
            screen: SCREENS.RESET_PASSWORD,
          });
        })
        .finally(() => {
          setIsButtonAnimating(false);
        });
    }
  };

  return (
    <ScreenContainer
      bottomActionComponent={
        <button className="login-button" onClick={goToLoginScreen}>
          {t('login')}
        </button>
      }
      errorKey={errorMessageKey}
      logoURL={logoURL}
      title={t('createNewTitle')}
    >
      <form className="create-password-form" onSubmit={onCreateNewPassword}>
        <PasswordField
          value={newPassword}
          label={t('newPassword')}
          placeholder={t('newPasswordPlaceholder')}
          onChange={(e) => {
            setNewPassword(e.currentTarget.value);
            setNewPasswordErrorKey(null);
          }}
          id="password"
          autoComplete="new-password"
          errorMessage={newPasswordErrorKey ? t(newPasswordErrorKey) : null}
          aria-describedby="password-requirements"
        />
        <PasswordField
          value={confirmedPassword}
          label={t('confirmPassword')}
          placeholder={t('confirmPasswordPlaceholder')}
          onChange={(e) => {
            setConfirmedPassword(e.currentTarget.value);
            setConfirmedPasswordErrorKey(null);
          }}
          id="confirmPassword"
          autoComplete="new-password"
          errorMessage={confirmedPasswordErrorKey ? t(confirmedPasswordErrorKey) : null}
          autoFocus={false}
        />
        <PasswordRequirements id="password-requirements" requirements={requirements} />
        <Button text={t('createNewButton')} isAnimating={isButtonAnimating}></Button>
      </form>
    </ScreenContainer>
  );
};

export default ResetPassword;

type PasswordValidationErrorKeys = {
  newPassword: string | null;
  confirmedPassword: string | null;
};

function passwordValidationErrorKeys({
  newPassword,
  confirmedPassword,
  areAllRequirementsSatisfied,
}: {
  newPassword: string;
  confirmedPassword: string;
  areAllRequirementsSatisfied: boolean;
}): PasswordValidationErrorKeys {
  if (!newPassword) {
    return {
      newPassword: 'resetErrors.required',
      confirmedPassword: !confirmedPassword ? 'resetErrors.required' : null,
    };
  } else if (!areAllRequirementsSatisfied) {
    return { newPassword: 'resetErrors.weak', confirmedPassword: null };
  } else if (!confirmedPassword) {
    return { newPassword: null, confirmedPassword: 'resetErrors.required' };
  } else if (newPassword !== confirmedPassword) {
    return { newPassword: null, confirmedPassword: 'resetErrors.mismatch' };
  } else {
    return { newPassword: null, confirmedPassword: null };
  }
}
